// packages/api/src/client.ts

import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';

import type { ApiError } from '@alamo/types';

import { clearMagicLinkSession, getMagicLinkBearer } from './public/magic-link-session';

/**
 * Access token stored in memory (not localStorage).
 * The refresh token is stored in localStorage (backend does not use httpOnly cookies).
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Refresh token storage. `@alamo/api` owns this key; `@alamo/auth` re-exports
 * these helpers from `./token` instead of redefining the literal.
 */
export const REFRESH_TOKEN_KEY = 'alamo_refresh_token';

export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredRefreshToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // localStorage not available
  }
}

export function clearStoredRefreshToken(): void {
  setStoredRefreshToken(null);
}

/**
 * Distingue erro de rede transient (API down, timeout, 5xx) de erro de auth
 * (401 do /auth/refresh, refresh token revogado).
 *
 * Usado nos catches de refresh boot pra NÃO destruir o refresh token quando
 * a API está apenas indisponível — usuário não deveria ser deslogado por
 * reboot do backend em dev/prod ou flap de rede.
 *
 * Erros considerados de rede (preservar tokens):
 *   - ECONNREFUSED / ECONNABORTED / ENOTFOUND / ETIMEDOUT (axios codes)
 *   - Network Error sem response (browser CORS preflight failure também cai aqui)
 *   - HTTP 502 / 503 / 504 (gateway/upstream issues)
 *
 * Erros de auth (limpar tokens + redirect /login):
 *   - 401 com response (refresh token rejeitado)
 *   - 422 / 400 (refresh token malformado)
 *   - Outros 4xx
 */
export function isNetworkError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status == null) {
    // Sem response = falha de transporte (ECONNREFUSED, timeout, DNS, CORS).
    return true;
  }
  return status === 502 || status === 503 || status === 504;
}

/**
 * Errors that do not prove the stored refresh token is invalid. Rate limiting
 * is temporary and must not erase a valid rotating token from the browser.
 */
export function isRecoverableRefreshError(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  return axios.isAxiosError(error) && error.response?.status === 429;
}

/**
 * Axios instance configured for the Alamo API.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // NestJS ValidationPipe com whitelist:true rejeita props com sufixo `[]`
  // (axios default v1+ serializa arrays como `key[]=v1&key[]=v2`).
  // Forçamos formato `repeat` (`key=v1&key=v2`) que NestJS interpreta como array.
  paramsSerializer: {
    indexes: null, // axios: null = repeat, true = bracketed indexes, false = brackets
  },
});

/**
 * Phase 09 Plan 09 (2026-05-18) — Axios para endpoints PÚBLICOS (sem auth).
 *
 * Usado por `usePublicProposalByToken` (`GET /p/:token`): o magic-link é
 * independente do JWT do consultor logado. Não injeta header `Authorization`
 * (não interceptors), não tenta refresh em 401 (que apareceria como
 * MAGIC_LINK_TOKEN_INVALID e dispararia loop com `apiClient`).
 *
 * Não usar para endpoints autenticados — esses ficam no `apiClient` padrão.
 */
export const publicApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null,
  },
});

/**
 * Erro que sinaliza sessão morta de verdade (refresh token corrente rejeitado
 * pelo backend, sem ter sido rotacionado por outro caminho). Os call-sites de
 * boot/refresh manual usam esta marca pra decidir entre "ir pra tela de login"
 * e "erro transient — preservar tokens".
 */
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Single-flight `POST /auth/refresh`. O backend ROTACIONA o refresh token a
 * cada chamada, então dois refresh concorrentes com o mesmo token levam o
 * segundo a um 401 (token já invalidado) = logout indevido. Esta função
 * garante que existe no máximo UM refresh em voo por vez: chamadas
 * concorrentes recebem a MESMA Promise.
 *
 * Usa `publicApiClient` (sem interceptors) pro POST, evitando (a) recursão no
 * interceptor de 401 e (b) o header `Authorization` stale do `apiClient`.
 *
 * Defense-in-depth multi-aba: guarda o refresh token USADO na chamada. Se o
 * refresh falhar com erro NÃO-de-rede, compara com o token corrente no
 * storage — se outra aba/caminho já rotacionou (token mudou), retenta UMA vez
 * com o token novo em vez de deslogar. Só limpa a sessão quando o token usado
 * ainda é o corrente E o erro é auth real.
 *
 * @returns o novo access token.
 * @throws {SessionExpiredError} sessão morta — quando não há refresh token no
 *   storage, quando o refresh token corrente é rejeitado pelo backend, ou
 *   quando o teto de 1 retry multi-aba é excedido. O access token e o refresh
 *   token já foram limpos do storage (refresh limpo via compare-and-clear).
 * @throws erro de rede original (tokens PRESERVADOS) quando `isNetworkError`.
 */
let refreshPromise: Promise<string> | null = null;

const MAX_REFRESH_RETRIES = 1;

/**
 * Marca a sessão como morta: zera o access token em memória e limpa o refresh
 * token do storage — mas só se o slot ainda contém `usedRefreshToken`
 * (compare-and-clear). Se outra aba já gravou um token NOVO, preserva-o.
 */
function markSessionExpired(usedRefreshToken: string): never {
  setAccessToken(null);
  if (getStoredRefreshToken() === usedRefreshToken) {
    setStoredRefreshToken(null);
  }
  throw new SessionExpiredError();
}

async function performRefresh(): Promise<string> {
  let usedRefreshToken = getStoredRefreshToken();
  if (!usedRefreshToken) {
    // Sem refresh token no storage = sessão morta; converge pro mesmo ramo de
    // logout que um refresh token rejeitado (evita escapar como erro genérico).
    setAccessToken(null);
    throw new SessionExpiredError();
  }

  // Laço com teto explícito de 1 retry (multi-aba): sai por return (sucesso)
  // ou throw. Cada retry só ocorre se outra aba rotacionou o token no storage.
  let attempts = 0;
  for (;;) {
    try {
      const response = await publicApiClient.post('/auth/refresh', {
        refresh_token: usedRefreshToken,
      });
      // Backend: { success, data: { accessToken, refreshToken }, timestamp }
      const newAccessToken: string = response.data.data.accessToken;
      const newRefreshToken: string = response.data.data.refreshToken;
      setAccessToken(newAccessToken);
      setStoredRefreshToken(newRefreshToken);
      return newAccessToken;
    } catch (error) {
      // Falha transitória: preserva tokens, deixa a próxima tentativa retentar.
      if (isRecoverableRefreshError(error)) {
        throw error;
      }

      // Auth real: antes de deslogar, checa se outra aba/caminho já rotacionou
      // o refresh token no storage. Se sim (e dentro do teto), retenta com o
      // token novo em vez de deslogar.
      const currentRefreshToken = getStoredRefreshToken();
      if (
        currentRefreshToken &&
        currentRefreshToken !== usedRefreshToken &&
        attempts < MAX_REFRESH_RETRIES
      ) {
        attempts += 1;
        usedRefreshToken = currentRefreshToken;
        continue;
      }

      // Token usado ainda é o corrente (ou teto excedido) + erro de auth =
      // sessão morta. Limpa via compare-and-clear pra não apagar token de outra
      // aba vencedora.
      markSessionExpired(usedRefreshToken);
    }
  }
}

export function refreshSession(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Skew de relógio pro refresh PROATIVO: renova o access token se faltar menos
 * que isto pra expirar (ou se já expirou). Absorve defasagem de relógio
 * cliente↔servidor sem depender de precisão de milissegundo.
 */
const ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;

/**
 * Lê o `exp` (epoch em segundos) de um JWT SEM verificar assinatura — só o
 * payload base64url. Retorna o instante de expiração em ms, ou null quando o
 * token não é um JWT decodificável (nesse caso o refresh proativo não dispara e
 * o fluxo reativo de 401 assume).
 */
function readJwtExpiryMs(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const json = JSON.parse(atob(base64 + '='.repeat(pad))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * TRUE quando o access token já expirou ou está dentro da janela de skew — o
 * sinal pro request interceptor renovar ANTES de enviar. Token sem `exp`
 * legível → FALSE (deixa o backend decidir via 401).
 */
function accessTokenNeedsRefresh(token: string): boolean {
  const expMs = readJwtExpiryMs(token);
  if (expMs == null) return false;
  return Date.now() >= expMs - ACCESS_TOKEN_REFRESH_SKEW_MS;
}

/**
 * Request interceptor: refresh PROATIVO + anexa o access token.
 *
 * Antes de enviar, se o access token já expirou (ou está a <30s de expirar) e
 * há refresh token, renova PRIMEIRO — o backend nunca chega a ver um token
 * vencido, eliminando o 401 previsível a cada ciclo de 15min. `refreshSession()`
 * é single-flight: N requests simultâneas com token vencido compartilham UM
 * único POST /auth/refresh. O refresh vai por `publicApiClient` (sem
 * interceptors), então não há recursão aqui.
 *
 * Se o refresh proativo falhar (sessão morta ou rede), segue com o estado
 * atual — o interceptor de resposta (401 → SessionExpiredError) mantém a
 * decisão de logout/preserve num único lugar.
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (accessToken && accessTokenNeedsRefresh(accessToken) && getStoredRefreshToken()) {
      try {
        await refreshSession();
      } catch {
        // noop — o fluxo reativo de 401 assume a partir daqui.
      }
    }
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response interceptor: handle 401 with automatic token refresh.
 * Um único refresh em voo por vez via `refreshSession()` — requests que tomam
 * 401 aguardam a MESMA Promise e retentam com o novo access token.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // 429 Rate Limiting — reject immediately, let callers handle
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // 403 with AUTH_ACCOUNT_DISABLED — reject immediately, let callers handle
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Tentativa de autenticação ATIVA do fluxo de login (sem sessão a expirar):
    // um 401 aqui é credencial/código inválido, tratado inline pelo formulário.
    // Recarregar apagaria a mensagem antes de ela ser pintada — então rejeita
    // sem refresh nem redirect.
    const isActiveAuthAttempt =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/2fa/complete') ||
      originalRequest.url?.includes('/auth/2fa/sms/send') ||
      originalRequest.url?.includes('/auth/forgot-password') ||
      originalRequest.url?.includes('/auth/reset-password');
    if (isActiveAuthAttempt) {
      return Promise.reject(error);
    }

    // Endpoints da própria maquinaria de sessão: um 401 aqui é sessão morta,
    // não "access token expirou" — desloga sem tentar refresh (evita loop).
    // `/auth/refresh` roda via publicApiClient e nem chega neste interceptor,
    // mas fica listado por clareza de intenção.
    //
    // ATENÇÃO: `/auth/me` e os `/auth/2fa/*` autenticados NÃO entram aqui — são
    // recursos protegidos normais e DEVEM se recuperar via refresh + retry
    // abaixo. O `includes('/auth/')` largo que existia aqui deslogava o usuário
    // a cada 401 de `/auth/me` (esperado a cada 15min), sem nunca renovar.
    if (
      originalRequest.url?.includes('/auth/logout') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      setAccessToken(null);
      setStoredRefreshToken(null);
      window.location.href = '/login';
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshSession();
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      // `refreshSession` já limpou os tokens em SessionExpiredError; erro de
      // rede preserva tokens (deixa a próxima request tentar de novo).
      if (refreshError instanceof SessionExpiredError) {
        window.location.href = '/login';
      } else if (isNetworkError(refreshError) && import.meta.env.DEV) {
        console.warn('[AUTH] refresh failed by network — preservando tokens', refreshError);
      }
      return Promise.reject(refreshError);
    }
  },
);

/**
 * Magic-link identity gate (ESCR-magic-link-auth) — interceptors do
 * `publicApiClient` APENAS. Não tocam `apiClient` nem `alamo_refresh_token`.
 *
 * Extrai o token-mãe (1º segmento após `/p/`) para requests da jornada pública.
 * O endpoint `POST /p/:token/verify-identity` é o emissor da sessão — nunca
 * leva Bearer nem dispara limpeza (é o único /p sem sessão).
 */
const MAGIC_LINK_SESSION_EXPIRED = 'MAGIC_LINK_SESSION_EXPIRED';
const MAGIC_LINK_SESSION_REQUIRED = 'MAGIC_LINK_SESSION_REQUIRED';
export const MAGIC_LINK_SESSION_INVALID_EVENT = 'alamo:magic-link-session-invalid';

function magicLinkTokenFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const path = url.split('?')[0] ?? url;
  if (path.endsWith('/verify-identity')) return null;
  const match = path.match(/^\/p\/([^/]+)/);
  return match?.[1] ?? null;
}

/**
 * Rotas `/p` protegidas por sub-token (`PublicWriteTokenGuard`), NÃO pela sessão
 * de identidade. NÃO podem receber o Bearer da sessão: o guard lê o header
 * `Authorization` primeiro e rejeitaria o session JWT (secret/scope distintos → 401).
 *  - pendências (GET/POST) → scope `documents_write`
 *  - documents batch/single → scope `documents_write` (mas `/documents/start` é RMS)
 */
function isSubTokenRoute(url: string | undefined): boolean {
  const path = (url ?? '').split('?')[0] ?? '';
  if (/\/pendencias(\/|$)/.test(path)) return true;
  if (/\/documents(\/|$)/.test(path) && !/\/documents\/start(\/|$)/.test(path)) {
    return true;
  }
  return false;
}

/**
 * Request: anexa `Authorization: Bearer <sessão>` às rotas `/p/:token(/*)`
 * marcadas com `@RequireMagicLinkSession` (exceto verify-identity e as rotas de
 * sub-token) quando há sessão válida em sessionStorage.
 */
publicApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = magicLinkTokenFromUrl(config.url);
    if (token && !isSubTokenRoute(config.url)) {
      const bearer = getMagicLinkBearer(token);
      if (bearer) {
        config.headers.Authorization = `Bearer ${bearer}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response: em 410 `MAGIC_LINK_SESSION_EXPIRED` ou 401 `MAGIC_LINK_SESSION_REQUIRED`
 * limpa a sessão do token e emite `alamo:magic-link-session-invalid` (o
 * MagicLinkGate escuta e remonta o desafio). Demais erros (429/422/AUTH_INVALID
 * + os codes do token-mãe) passam intactos. Sempre rejeita no fim.
 */
publicApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ code?: string }>) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const isExpired = status === 410 && code === MAGIC_LINK_SESSION_EXPIRED;
    const isRequired = status === 401 && code === MAGIC_LINK_SESSION_REQUIRED;
    if (isExpired || isRequired) {
      const token = magicLinkTokenFromUrl(error.config?.url);
      if (token) {
        clearMagicLinkSession(token);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(MAGIC_LINK_SESSION_INVALID_EVENT, { detail: { token } }),
          );
        }
      }
    }
    return Promise.reject(error);
  },
);
